import { useWindowContext } from '@/hooks/WindowSizeContext/useWindowContext';
import FilterBar from '../Filter/Filterbar';
import Label from '../UI/Label';
import styles from './DrilldownGraph.module.css';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useFilterDateContext } from '@/hooks/FilterDateContext/useFilterDateContext';
import { useTranslation } from 'react-i18next';
import CustomLoader from '../UI/CustomLoader';
import Button from '../UI/Button';
import { useRemoteGet } from '@/hooks/useRemoteGet';
import { CustomToolsenseResourceService } from '@/services/openapi';
import {
	formDateToTimestamp,
	formatTime,
	getHourFromTimestamp,
	formatDate
} from '@/utils';
import { ComboSelectProps } from '../UI/Select/ComboSelect/ComboSelect';
import { SelectInstance, SingleValue } from 'react-select';
import { SelectDataProps } from '../UI/Select/SelectBox';
import { parseISO } from 'date-fns';

interface DrilldownGraphProps {
	rin: string | undefined; // Toolsense AssetID
	className?: string;
}

const DrilldownGraph = (props: DrilldownGraphProps) => {
	const { t } = useTranslation();
	const { size, isMobile } = useWindowContext();

	const {
		date1: date1Default,
		date2: date2Default,
		timezone
	} = useFilterDateContext();

	// Tooltip all'hover sull'intervallo
	const [tooltipInfo, setTooltipInfo] = useState<{
		visible: boolean;
		text: string;
		x: number;
		y: number;
		dayIndex: number | null;
	}>({
		visible: false,
		text: '',
		x: 0,
		y: 0,
		dayIndex: null
	});

	const [parameter, setParameter] = useState('runtime');
	const parameterRef = useRef<SelectInstance<SelectDataProps> | null>(null);

	const [dateStart, setDateStart] = useState(date1Default);
	const dateStartRef = useRef(null);

	const [dateEnd, setDateEnd] = useState(date2Default);
	const dateEndRef = useRef(null);

	const buttonRefUsage = useRef<HTMLButtonElement>(null);
	const [errorDataPeriod, setErrorDataPeriod] = useState(false);

	// Riferimento per il contenitore del grafico
	const graphContainerRef = useRef<HTMLDivElement>(null);

	const comboDateUsage = {
		idDate1: 'data1',
		nameDate1: 'Data1',
		valueDate1: dateStart,
		idDate2: 'data2',
		nameDate2: 'Data2',
		valueDate2: dateEnd,
		min: 'string',
		max: 'string',
		labelName: 'string',
		textLabel: t('dashboard.period'),
		ref1: dateStartRef,
		onChangeDate1: (e: any) => {
			setDateStart(e.target.value);
		},
		ref2: dateEndRef,
		onChangeDate2: (e: any) => {
			setDateEnd(e.target.value);
		}
	};

	const selectParameter: ComboSelectProps = {
		selectData: [
			{ value: 'Runtime', label: 'Runtime' },
			{ value: 'Utilization', label: 'Utilization' },
			{ value: 'Work done', label: 'Work Done' },
			{ value: 'Brush motor', label: 'Brush motor' },
			{ value: 'Asset Battery Voltage', label: 'Asset Battery Voltage' },
			{ value: 'Module Signal Strength', label: 'Module Signal Strength' },
			{ value: 'Module Battery Level', label: 'Module Battery Level' }
		],
		textLabel: t('machine.toolsense.parameter'),
		nameSelect: 'selectParameter',
		firstOption: 'Runtime',
		ref: parameterRef,
		valueSelected: { value: parameter, label: parameter },
		onChange: (newValue, actionMeta) => {
			const singleValue = newValue as SingleValue<SelectDataProps>;
			if (singleValue !== null && actionMeta.action === 'select-option') {
				setParameter(singleValue.value);
			}
		}
	};

	const assetDailyData = useRemoteGet({
		remoteMethod: () => {
			if (typeof props.rin !== 'undefined') {
				return CustomToolsenseResourceService.getAssetDailyData(
					props.rin,
					parameter, // ParameterName (Runtime, Utilization, Work done)
					formDateToTimestamp(dateStart),
					formDateToTimestamp(dateEnd, true),
					timezone
				);
			}
		},
		lazy: true
	});

	// Al caricamento del rin fa la fetch
	useEffect(() => {
		if (props.rin) {
			assetDailyData.fetch();
		}
	}, [props.rin]);

	/*
	In base alla sezione in cui vengono modificate le date nel campo di ricerca
	controlla i valori delle date ed effettua le query al db
	*/
	const applyFilters = useCallback(() => {
		if (dateStart <= dateEnd) {
			setErrorDataPeriod(false);
			if (!assetDailyData.loading) assetDailyData.fetch();
		} else {
			setErrorDataPeriod(true);
			setTimeout(() => {
				setErrorDataPeriod(false);
			}, 2000);
		}
	}, [dateStart, dateEnd, parameter]);

	const loader = () => {
		if (assetDailyData.loading)
			return (
				<span>
					<CustomLoader searchLoader={true} />
				</span>
			);
		else return <span></span>;
	};

	// -------------------------------------------------------------------------------------------------------------------
	// FORMATTATORI ORARI
	// Funzione per calcolare il totale di ore di utilizzo
	const calculateTotalHours = (totalUsage: number, avgVal: number) => {
		const hours = Math.floor(totalUsage / 60);
		const minutes = Math.floor(totalUsage % 60);

		switch (parameter) {
			case 'Runtime':
			case 'Brush motor':
				return `${hours}h:${minutes < 10 ? '0' + minutes : minutes}m`;
			case 'Utilization':
				return `${hours}.${minutes < 10 ? '0' + minutes : minutes}%`;
			case 'Work done':
				return `${hours}.${minutes} m²`;
			case 'Asset Battery Voltage':
			case 'Module Signal Strength':
			case 'Module Battery Level':
				return `Avg: ${Math.floor(avgVal)}`;
			default:
				return `${hours}h:${minutes < 10 ? '0' + minutes : minutes}m`;
		}
	};

	return (
		<div className={styles.box_subContainer} id="usageGraph">
			{!isMobile && (
				<Label text={t('machine.usageGraph')} className={styles.titleLabel} />
			)}
			<div className={styles.tableHeader}>
				<FilterBar
					filters={[
						{
							type: 'Date',
							selectData: comboDateUsage,
							boxClassName: '',
							dataError: errorDataPeriod
						},
						{
							type: 'Select',
							selectData: selectParameter,
							boxClassName: styles.select_container
						}
					]}
				/>
			</div>
			<div className={styles.searchBtnContainer}>
				<Button
					ref={buttonRefUsage}
					name={t('dashboard.applyFilters')}
					className={styles.searchBtn}
					onClick={() => applyFilters()}
				/>
				{loader()}
			</div>

			{/* CONTENITORE del grafico */}
			<div className={styles.graphContainer} ref={graphContainerRef}>
				{assetDailyData.data && assetDailyData.data.length > 0 ? (
					<div className={styles.graph}>
						{/* INTESTAZIONE oraria */}
						<div className={styles.timeHeader}>
							{/* Cella vuota in alto a sx */}
							<div className={styles.dayLabel}>
								<div className={styles.dayDate}></div>
							</div>

							{/* Celle delle ore */}
							<div className={styles.timeHeaderCells}>
								{Array.from({ length: 24 }, (_, i) => (
									<div key={`time-${i}`} className={styles.timeCell}>
										{`${i}:00`}
									</div>
								))}
							</div>
						</div>

						{/* DATI: Righe per ogni giorno */}
						{assetDailyData.data.map((dayData, dayIndex) => (
							<div
								key={`day-${dayIndex}`}
								className={`${styles.dayRow} ${
									tooltipInfo.dayIndex === dayIndex ? styles.highlightedRow : ''
								}`}
							>
								{/* 1a colonna: Etichetta giorno e totale ore */}
								<div className={styles.dayLabel}>
									<div className={styles.dayDate}>{formatDate(dayData.day!)}</div>
									<div className={styles.dayTotal}>
										{calculateTotalHours(dayData.totalUsage!, dayData.averageVal!)}
									</div>
								</div>

								{/* Altre colonne: Cella per gli intervalli */}
								<div className={styles.intervalsContainer}>
									{/* Contenitore a griglia per le ore */}
									<div className={styles.hoursGrid}>
										{Array.from({ length: 24 }, (_, hourIndex) => {
											// Filtra gli intervalli che rientrano in quest'ora
											const hourIntervals = dayData.intervals?.filter((interval) => {
												const startHour = getHourFromTimestamp(interval.start!);
												const endHour = getHourFromTimestamp(interval.end!);
												return (
													startHour === hourIndex ||
													(endHour > startHour &&
														hourIndex >= startHour &&
														hourIndex <= endHour)
												);
											});

											return (
												<div key={`hour-${hourIndex}`} className={styles.hourCell}>
													{hourIntervals!.length > 0 && (
														<div className={styles.activeHour}></div>
													)}
												</div>
											);
										})}
									</div>

									{/* Visualizzazione intervalli */}
									{dayData.intervals?.map((interval, intervalIndex) => {
										const startHour = getHourFromTimestamp(interval.start!);
										const endHour = getHourFromTimestamp(interval.end!);
										const startMinute = parseISO(interval.start!).getUTCMinutes();
										const endMinute = parseISO(interval.end!).getUTCMinutes();

										// Calcola la posizione e dimensione dell'intervallo
										const startPosition = ((startHour + startMinute / 60) * 100) / 24;
										const endPosition = ((endHour + endMinute / 60) * 100) / 24;
										const width = endPosition - startPosition;

										const tooltipText = `${formatTime(interval.start!)} - ${formatTime(
											interval.end!
										)}`;

										return (
											<div
												key={`interval-${intervalIndex}`}
												className={styles.interval}
												style={{
													left: `${startPosition}%`,
													width: `${width}%`
												}}
												onMouseEnter={(e) => {
													const rect = e.currentTarget.getBoundingClientRect();
													setTooltipInfo({
														visible: true,
														text: tooltipText,
														x: rect.left + rect.width / 2,
														y: rect.top,
														dayIndex: dayIndex
													});
												}}
												onMouseLeave={() => {
													setTooltipInfo({ ...tooltipInfo, visible: false, dayIndex: null });
												}}
											></div>
										);
									})}
								</div>
							</div>
						))}
					</div>
				) : (
					<div className={styles.noData}>
						{assetDailyData.loading ? (
							<CustomLoader />
						) : (
							<p>{t('machine.toolsense.noDataAvailable')}</p>
						)}
					</div>
				)}

				{/* Tooltip per orari intervallo */}
				{tooltipInfo.visible && (
					<div
						className={styles.customTooltip}
						style={{
							position: 'fixed',
							left: `${tooltipInfo.x}px`,
							top: `${tooltipInfo.y}px`
						}}
					>
						{tooltipInfo.text}
					</div>
				)}
			</div>
		</div>
	);
};

export default DrilldownGraph;
